/*
 * Autorska prava pridržava Mali mol d.o.o. Pula
 * Copyright (c) 2016. by Mali mol d.o.o. Pula. All rights reserved.
 *
 * Korištenje ili javno objavljivanje ovog programskog koda ili bilo kojeg
 * njegovog dijela, nije dozvoljeno bez prethodnog pismenog odobrenja tvrtke Mali mol d.o.o.
 *
 * Mali mol d.o.o., Špiljavac 2, 52100 Pula, Croatia
 * <URL:http://www.malimol.hr>, <mailto:info@malimol.hr>
 */

import { defineComponent, watch, onMounted, ref } from "vue";
import { usePrimeVue } from "primevue/config";
import { useI18n } from "vue-i18n";
import { v4 as uuidv4 } from "uuid";
import { useAuthStore } from "../../stores/auth";
import { useAppStore } from "../../stores/app";
import { SceneController } from "../../controllers/SceneController";
import Navbar from "../Navbar.vue";
import WasmUpdateBanner from "../WasmUpdateBanner.vue";
import { i18n } from "../../app";
import { loadLocaleMessages } from "../../i18n-setup";
import { RouteRecordName } from "vue-router";
import enUrl from "@locales/ui.en.json?url";
import deUrl from "@locales/ui.de.json?url";
import hrUrl from "@locales/ui.hr.json?url";

declare var Module: {
  fontAppearance: any;
  view: any;
  canvas: null | HTMLCanvasElement;
  ready: Promise<void>;
  reset: any;
  sceneController: SceneController;
  writeUserConfig: any;
};

/**
 * App komponenta je glavna komponenta koja se prikazuje na svim stranicama. Ona sadrži navigacijsku traku i
 * komponentu koja se mijenja ovisno o odabranoj ruti.
 */
export default defineComponent({
  name: "App",
  components: {
    Navbar,
    WasmUpdateBanner,
  },
  setup() {
    const { t, locale } = useI18n({ useScope: "global" });
    const authStore = useAuthStore();
    const appStore = useAppStore();
    const primevue = usePrimeVue();

    const reloaded = ref(authStore.userReplaced);
    const userId = ref(uuidv4());

    onMounted(() => {
      console.log("App mounted");
      // console.log(ls.value);
      setUILocale(locale.value as string);
    });

    watch(() => authStore.userReplaced, (isReplaced) => {
      if (isReplaced) {
        console.log("User has been switched: forcing scene rebuild");
        Module.view.clearSketch();
        userId.value = uuidv4();
        authStore.loggedIn();
      }
    });

    /**
     * Postavlja jezik za UI i učitava poruke za taj jezik. To su prijevodi za PrimeVue komponente.
     * @param lang string sa oznakom jezika
     */
    const setUILocale = (lang: string) => {
      // console.log("setUILocale = " + lang);
      if (appStore.language != lang) appStore.changeLanguage(lang);
      loadLocaleMessages(i18n, lang).then(() => {
        loadMessages(lang)
          .then((json) => {
            // Primevue je malo zajebo stvar jer je krivo deklarirao tip podatka
            (primevue as any).config.locale = json;
          })
          .then(() => {
            locale.value = lang;
          })
          .catch((e) => {
            console.log(e);
          });
      });
    };

    /**
     * Vraća ključ koji se koristi za keeplive komponente. Ključ se sastoji od imena rute i id-a korisnika osim ako se
     * radi o ruti Scene koja je uvijek ista kako se nebi resetirala scena prilikom promjene korisnika jer to onda
     * povlači za sobom i resetiranje OPenGL contexta.
     * @param routeName naziv rute
     * @returns string sa ključem
     */
    const getKeyId = (
      routeName: RouteRecordName | null | undefined
    ): string => {
      if (routeName) {
        let name = routeName.toString();
        if (name === "Scene") return name;
        else return name + "-" + userId.value;
      } else {
        return userId.value;
      }
    };

    const getFileUrl = (locale: string): string => {
      switch (locale) {
        case "en":
          return enUrl;
        case "de":
          return deUrl;
        case "hr":
          return hrUrl;
        default:
          return hrUrl; // Default to Croatian
      }
    };

    /**
     * Učitava poruke za odabrani jezik
     * @param lang string sa oznakom jezika
     * @returns
     */
    const loadMessages = async (lang: string) => {
      // load locale messages with dynami import
      let url = getFileUrl(lang);
      // console.log("url: ", url);
      return fetch(url).then((res) => res.json());
    };

    return {
      reloaded,
      userId,
      getKeyId,
      setUILocale,
    };
  },
});
